import { useState, useEffect } from 'react';
import { Calendar, Link as LinkIcon, FileText, Building2, Target, TrendingUp, TrendingDown, Shield } from 'lucide-react';
import type { ProjectoCampaign, ProjectoStatus } from '../../types/projecto';
import { PROJECTO_STATUS_CONFIG } from '../../types/projecto';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Client {
  id: string;
  name: string;
}

interface Props {
  data: Partial<ProjectoCampaign>;
  onChange: (updates: Partial<ProjectoCampaign>) => void;
  actualMarginPct?: number;
}

export function ProjectoGeneralTab({ data, onChange, actualMarginPct = 0 }: Props) {
  const [clients, setClients] = useState<Client[]>([]);
  const { isAdmin } = useAuth();

  useEffect(() => {
    supabase.from('clients').select('id, name').order('name').then(({ data }) => {
      setClients(data ?? []);
    });
  }, []);

  const handleClientChange = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    onChange({ client_id: clientId || null, client_name: client?.name ?? '' });
  };

  const field = (
    label: string,
    content: React.ReactNode,
    required = false
  ) => (
    <div className="cm-field">
      <label className="cm-label">
        {label}{required && <span className="cm-req">*</span>}
      </label>
      {content}
    </div>
  );

  const inputCls = 'cm-input';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {field('Project Name', (
          <input
            data-required-field="projecto-name"
            className={inputCls}
            placeholder="e.g. Summer Festival 2026"
            value={data.name ?? ''}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        ), true)}

        {field('Reference / Code', (
          <input
            className={inputCls}
            placeholder="e.g. PRJ-2026-001"
            value={data.reference ?? ''}
            onChange={(e) => onChange({ reference: e.target.value })}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {field('Client', (
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              className="cm-select pl-9"
              value={data.client_id ?? ''}
              onChange={(e) => handleClientChange(e.target.value)}
            >
              <option value="">— Select client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        ))}

        {field('Brand', (
          <input
            className={inputCls}
            placeholder="e.g. Nike, Heineken"
            value={data.brand ?? ''}
            onChange={(e) => onChange({ brand: e.target.value })}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {field('Status', (
          <select
            className="cm-select"
            value={data.status ?? 'Draft'}
            onChange={(e) => onChange({ status: e.target.value as ProjectoStatus })}
          >
            {Object.entries(PROJECTO_STATUS_CONFIG).map(([s, cfg]) => (
              <option key={s} value={s}>{cfg.label}</option>
            ))}
          </select>
        ))}

        {field('Start Date', (
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              className="cm-date pl-9"
              value={data.start_date ?? ''}
              onChange={(e) => {
                const startVal = e.target.value || null;
                const updates: Partial<ProjectoCampaign> = { start_date: startVal };
                if (startVal) {
                  const nextDay = new Date(startVal);
                  nextDay.setDate(nextDay.getDate() + 1);
                  const nextDayStr = nextDay.toISOString().split('T')[0];
                  if (!data.finish_date || data.finish_date <= startVal) {
                    updates.finish_date = nextDayStr;
                  }
                }
                onChange(updates);
              }}
            />
          </div>
        ))}

        {field('Finish Date', (
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              className="cm-date pl-9"
              value={data.finish_date ?? ''}
              onChange={(e) => onChange({ finish_date: e.target.value || null })}
            />
          </div>
        ))}
      </div>

      {field('Drive / Files Link', (
        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="url"
            className="cm-input pl-9"
            placeholder="https://drive.google.com/..."
            value={data.drive_link ?? ''}
            onChange={(e) => onChange({ drive_link: e.target.value || null })}
          />
        </div>
      ))}

      {field('Internal Notes / Briefing', (
        <div className="relative">
          <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
          <textarea
            rows={4}
            className="cm-textarea pl-9"
            placeholder="Add any project notes, briefing context, or objectives..."
            value={data.notes ?? ''}
            onChange={(e) => onChange({ notes: e.target.value || null })}
          />
        </div>
      ))}

      {isAdmin && (
        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm font-semibold text-gray-700">Admin-only visibility</p>
                <p className="text-[11px] text-gray-400">
                  When enabled, this project is hidden from non-admin users.
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={data.admin_only ?? false}
              onClick={() => onChange({ admin_only: !(data.admin_only ?? false) })}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                data.admin_only ? 'bg-accent-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                  data.admin_only ? 'translate-x-[18px]' : 'translate-x-[3px]'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Budget & Margin Target</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={data.budget_enabled ?? false}
            onClick={() => onChange({ budget_enabled: !(data.budget_enabled ?? false) })}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              data.budget_enabled ? 'bg-accent-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                data.budget_enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
              }`}
            />
          </button>
        </div>

        {data.budget_enabled ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Total Budget
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">{'\u20AC'}</span>
                <input
                  type="number"
                  min="0"
                  step="100"
                  className={`${inputCls} pl-8`}
                  placeholder="0"
                  value={data.budget_amount ?? 0}
                  onChange={(e) => onChange({ budget_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Target Margin %
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={inputCls}
                  placeholder="0"
                  value={data.target_margin_pct ?? 0}
                  onChange={(e) => onChange({ target_margin_pct: parseFloat(e.target.value) || 0 })}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
              </div>
            </div>
            <div className="flex items-end pb-1">
              {(data.target_margin_pct ?? 0) > 0 && (
                <div className="flex items-center gap-2">
                  {actualMarginPct >= (data.target_margin_pct ?? 0) ? (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                      <TrendingUp className="w-3 h-3" />
                      {actualMarginPct.toFixed(1)}% actual
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600">
                      <TrendingDown className="w-3 h-3" />
                      {actualMarginPct.toFixed(1)}% actual
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">
            Enable to set a total budget and target margin for this project.
          </p>
        )}
      </div>
    </div>
  );
}
